import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, forwardRef, useCallback, useMemo, useRef, useContext } from 'react';
import classnames from 'classnames';
import { createUseStyles, Select } from '@Sage/hoi-poi-ui';
import { getList } from "../../../../lib/services/valueLists";
import { useWeb5Context } from "../../../../lib/Web5Context";
import { MATCHING_VALUES } from "../../constants";
import ImportContext from "./../../ImportContext";

// Select component doesn't handle negative integers, and backend need them. So...
// All the values that come from props (data, options, matchOptions...) should come as Integers
// All the values and options that are passed to the Select component should be Strings
import { transformToString, transformToInteger } from "../utils/transformValue";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'FieldFormatter');
const FuzzyFieldFormatter = (_ref, ref) => {
  var _node$data3;
  let {
    data,
    className,
    options,
    matchOptions,
    onChange,
    fieldTable,
    node,
    api
  } = _ref;
  const classes = useStyles();
  const fieldClassName = classnames(className, classes.field);
  const {
    t,
    language: lang
  } = useWeb5Context();
  const {
    language: externalLanguage
  } = useContext(ImportContext);
  const language = externalLanguage || lang;
  const selectRef = useRef();
  const isCreate = useMemo(() => data.nameField === MATCHING_VALUES.creatAsNew, [data]);
  const value = useMemo(() => {
    if (!data.nameField && data.nameField !== 0 || data.nameField === MATCHING_VALUES.noValue) return null;
    if (data.nameField === MATCHING_VALUES.creatAsNew) {
      return {
        label: t('cfm_label_create_as_new_one'),
        value: transformToString(MATCHING_VALUES.creatAsNew)
      };
    }
    const optionExists = options.find(option => transformToString(option.value) === transformToString(data.nameField));
    if (!optionExists && data.name && data.nameField) {
      var _node$data, _node$data2;
      options.push({
        label: (_node$data = node.data) === null || _node$data === void 0 ? void 0 : _node$data.label,
        value: transformToString(data === null || data === void 0 ? void 0 : data.nameField)
      });
      return {
        label: (_node$data2 = node.data) === null || _node$data2 === void 0 ? void 0 : _node$data2.label,
        value: transformToString(data === null || data === void 0 ? void 0 : data.nameField)
      };
    }
    return optionExists;
  }, [data.nameField, data.name, t, options, (_node$data3 = node.data) === null || _node$data3 === void 0 ? void 0 : _node$data3.label]);
  const finalOptions = useCallback(() => {
    return options.map(current => ({
      label: current.label,
      value: transformToString(current.value)
    }));
  }, [options]);
  const onLoadOptions = useCallback(searchText => {
    return getList({
      list: fieldTable,
      language,
      searchText
    }).then(_ref2 => {
      let {
        data
      } = _ref2;
      let finalOptions = data.map(item => {
        return {
          label: item.description,
          value: transformToString(item.id)
        };
      });
      return finalOptions;
    }).catch(console.error);
  }, [fieldTable, language]);
  const onChangeSelect = useCallback(option => {
    onChange(data.index, option ? transformToInteger(option.value) : MATCHING_VALUES.noValue);
    node.setData({
      name: data.name,
      nameField: transformToInteger(option === null || option === void 0 ? void 0 : option.value) || MATCHING_VALUES.noValue,
      label: option === null || option === void 0 ? void 0 : option.label
    });
    setTimeout(() => api.refreshCells({
      columns: ['nameField'],
      force: true
    }), 100);
  }, [api, data, node, onChange]);
  const rsOverrides = useMemo(() => isCreate ? {
    menuIsOpen: false
  } : {}, [isCreate]);
  const overrides = useMemo(() => ({
    control: {
      style: {
        background: 'none',
        border: 'none',
        '&:hover': {
          cursor: isCreate ? 'initial' : 'pointer'
        }
      }
    },
    controlFocused: {
      style: {
        background: 'none',
        border: 'none'
      }
    },
    clearIndicator: {
      style: {
        cursor: 'pointer'
      }
    },
    'react-select': _objectSpread({}, rsOverrides)
  }), [isCreate, rsOverrides]);
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: fieldClassName
  }, /*#__PURE__*/React.createElement(Select, {
    getRef: ref => {
      selectRef.current = ref;
    },
    className: classes.select,
    placeholder: t('cfm_placeholder_select_one_match'),
    options: finalOptions,
    onChange: onChangeSelect,
    value: value,
    isFullWidth: true,
    isFuzzy: !isCreate,
    loadOptions: matchOptions ? onLoadOptions : null,
    hideDropdownIndicator: true,
    isSearchable: !isCreate,
    isReadOnly: (value === null || value === void 0 ? void 0 : value.value) === transformToString(MATCHING_VALUES.creatAsNew),
    overrides: _objectSpread({}, overrides)
  }));
};
export default /*#__PURE__*/memo(/*#__PURE__*/forwardRef(FuzzyFieldFormatter));